import React, { useMemo, useState } from 'react';

// Translations for multilingual support (en, de, cs_cz)
const TRANSLATIONS = {
  en: {
    login: 'Login',
    username: 'Username',
    password: 'Password',
    loginError: 'Invalid username or password',
    exampleValue: 'Example value',
    calculationValue: 'Calculation value',
    result: 'Result',
    input: 'Input',
    rooms: 'Rooms',
    area: 'Area',
    attributes: 'Attributes',
    attrRaumstempel: '1. Attributes from space stamp (raumstempel)',
    attrExcel: '2. Excel file',
    customAttrCount: '3. Custom attribute count',
    coefficientOfDifficulty: 'Coefficient of difficulty',
    difficultyDescription: 'Defines the geometric complexity of the building — from simple box-shaped rooms to highly complex structures like cathedrals.',
    sourceMaterials: 'Source materials',
    plansAndSections: '1. Plans and sections',
    handDrawn: '1.1. Hand drawn',
    dwg: '1.2. DWG',
    pointcloud: '2. Pointcloud',
    plansPointcloud: '3. Plans and sections - Pointcloud',
    handDrawnPointcloud: '3.1. Hand drawn + Pointcloud',
    dwgPointcloud: '3.2. DWG + Pointcloud',
    ifcImport: '4. IFC import',
    internal: 'Internal',
    internalHourlyRate: 'Internal hourly rate',
    provision: 'Provision',
    marge: 'Marge',
    partIoLabs: 'Part ioLabs',
    baseWorkspeed: 'Base workspeed [rooms/h]',
    management: 'Management',
    exclVat: '(excl. VAT)'
  },
  de: {
    login: 'Anmelden',
    username: 'Benutzername',
    password: 'Passwort',
    loginError: 'Ungültiger Benutzername oder Passwort',
    exampleValue: 'Beispielwert',
    calculationValue: 'Berechnungswert',
    result: 'Ergebnis',
    input: 'Eingabe',
    rooms: 'Räume',
    area: 'Fläche',
    attributes: 'Attribute',
    attrRaumstempel: '1. Attribute aus Raumstempel',
    attrExcel: '2. Excel-Datei',
    customAttrCount: '3. Anzahl benutzerdefinierter Attribute',
    coefficientOfDifficulty: 'Schwierigkeitskoeffizient',
    difficultyDescription: 'Definiert die geometrische Komplexität des Gebäudes — von einfachen quaderförmigen Räumen bis zu hochkomplexen Strukturen wie Kathedralen.',
    sourceMaterials: 'Quellmaterialien',
    plansAndSections: '1. Pläne und Schnitte',
    handDrawn: '1.1. Handgezeichnet',
    dwg: '1.2. DWG',
    pointcloud: '2. Punktwolke',
    plansPointcloud: '3. Pläne und Schnitte - Punktwolke',
    handDrawnPointcloud: '3.1. Handgezeichnet + Punktwolke',
    dwgPointcloud: '3.2. DWG + Punktwolke',
    ifcImport: '4. IFC-Import',
    internal: 'Intern',
    internalHourlyRate: 'Interner Stundensatz',
    provision: 'Provision',
    marge: 'Marge',
    partIoLabs: 'Anteil ioLabs',
    baseWorkspeed: 'Basis-Arbeitsgeschwindigkeit [Räume/h]',
    management: 'Management',
    exclVat: '(exkl. MwSt.)'
  },
  cs: {
    login: 'Přihlášení',
    username: 'Uživatelské jméno',
    password: 'Heslo',
    loginError: 'Neplatné uživatelské jméno nebo heslo',
    exampleValue: 'Příkladová hodnota',
    calculationValue: 'Vypočtená hodnota',
    result: 'Výsledek',
    input: 'Vstup',
    rooms: 'Místnosti',
    area: 'Plocha',
    attributes: 'Atributy',
    attrRaumstempel: '1. Atributy ze značky místnosti (raumstempel)',
    attrExcel: '2. Excel soubor',
    customAttrCount: '3. Počet vlastních atributů',
    coefficientOfDifficulty: 'Koeficient obtížnosti',
    difficultyDescription: 'Definuje geometrickou složitost budovy — od jednoduchých krabicových místností až po vysoce složité struktury jako katedrály.',
    sourceMaterials: 'Zdrojové materiály',
    plansAndSections: '1. Půdorysy a řezy',
    handDrawn: '1.1. Ručně kreslené',
    dwg: '1.2. DWG',
    pointcloud: '2. Bodový mrak',
    plansPointcloud: '3. Půdorysy a řezy - Bodový mrak',
    handDrawnPointcloud: '3.1. Ručně kreslené + Bodový mrak',
    dwgPointcloud: '3.2. DWG + Bodový mrak',
    ifcImport: '4. IFC import',
    internal: 'Interní',
    internalHourlyRate: 'Interní hodinová sazba',
    provision: 'Provize',
    marge: 'Marže',
    partIoLabs: 'Podíl ioLabs',
    baseWorkspeed: 'Základní rychlost práce [místnosti/h]',
    management: 'Management',
    exclVat: '(bez DPH)'
  }
} as const;
type Lang = keyof typeof TRANSLATIONS;

// Format amount in Swiss francs (CHF before amount, Swiss locale)
function formatCHF(amount: number): string {
  return amount.toLocaleString('de-CH', {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

// Checkbox component using corporate brand blue
function Checkbox({
  checked,
  onChange,
  disabled,
  className = ''
}: {
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  className?: string;
}) {
  return <label className={`relative inline-flex cursor-pointer ${disabled ? 'cursor-not-allowed opacity-40' : ''} ${className}`} data-id="element-0">
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} className="sr-only peer" data-id="element-1" />
      <span className={`relative inline-block w-5 h-5 min-w-5 min-h-5 shrink-0 rounded border border-gray-400 bg-white peer-checked:border peer-checked:border-brand peer-checked:bg-brand peer-focus-visible:ring-2 peer-focus-visible:ring-brand peer-focus-visible:ring-offset-0 transition-colors box-border ${disabled ? 'pointer-events-none' : ''}`} data-id="element-2">
        {checked && <svg className="absolute inset-0 m-auto w-3.5 h-3.5 text-white pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-3">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-4" />
          </svg>}
      </span>
    </label>;
}
export function App() {
  // Language from URL param ?lang (en default, de, cs_cz supported)
  const lang = useMemo((): Lang => {
    const params = new URLSearchParams(window.location.search);
    const param = params.get('lang');
    if (param === 'de' || param === 'cs') return param;
    return 'en';
  }, []);
  const t = TRANSLATIONS[lang];

  // Check if public mode is enabled via URL parameter
  const isPublicMode = new URLSearchParams(window.location.search).has('public');
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (isPublicMode) return true; // Skip auth for public mode
    return sessionStorage.getItem('authenticated') === 'true';
  });
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  // Input section (formerly Rooms)
  const [rooms, setRooms] = useState(863); // F1
  const [area, setArea] = useState(37095); // F2
  // Attributes section - master checkbox
  const [attributesEnabled, setAttributesEnabled] = useState(true);
  const [attrRaumstempel, setAttrRaumstempel] = useState(true); // F3 - true when attributesEnabled is true
  const [attrExcelFile, setAttrExcelFile] = useState(false); // F4
  const [customAttrCount, setCustomAttrCount] = useState(1); // Number of attributes
  // Calculate coefficient based on number of attributes
  const calculateAttrCoefficient = (n: number) => {
    if (n === 1) return 0.7;
    if (n === 2) return 0.85;
    if (n === 3) return 1;
    if (n === 4) return 1.2;
    if (n >= 5) return 1.3 + n * 0.1;
    return 0; // for other values
  };
  // Coefficient of difficulty
  const [difficulty, setDifficulty] = useState(1); // F6
  // Source materials (Yes/No) - only one should be selected
  const [srcHandDrawn, setSrcHandDrawn] = useState(false); // F7 = 1.5
  const [srcDWG, setSrcDWG] = useState(true); // F8 = 1.1
  const [srcPointcloud, setSrcPointcloud] = useState(false); // F9 = 2
  const [srcHandDrawnPointcloud, setSrcHandDrawnPointcloud] = useState(false); // F10 = 2.2
  const [srcDWGPointcloud, setSrcDWGPointcloud] = useState(false); // F11 = 1.5
  const [srcIFC, setSrcIFC] = useState(false); // F12 = 0.2
  // Internal section
  const [hourlyRate, setHourlyRate] = useState(55); // E1
  const [provision, setProvision] = useState(0.25); // E2
  const [marge, setMarge] = useState(4); // E3 - editable
  const [baseWorkspeed, setBaseWorkspeed] = useState(23); // E4
  const [management, setManagement] = useState(1.25); // E5
  // Multiplier values - now editable
  const [F7, setF7] = useState(1.5);
  const [F8, setF8] = useState(1.1);
  const [F9, setF9] = useState(2);
  const [F10, setF10] = useState(2.3);
  const [F11, setF11] = useState(2.2);
  const [F12, setF12] = useState(0.1);
  // Attribute values - now editable
  const [F3_val, setF3_val] = useState(0);
  const [F4_val, setF4_val] = useState(0.6);
  const [F5_val, setF5_val] = useState(0.5);
  const result = useMemo(() => {
    // Get selected source material multiplier
    let sourceMultiplier = 0;
    if (srcHandDrawn) sourceMultiplier = F7;else if (srcDWG) sourceMultiplier = F8;else if (srcPointcloud) sourceMultiplier = F9;else if (srcHandDrawnPointcloud) sourceMultiplier = F10;else if (srcDWGPointcloud) sourceMultiplier = F11;else if (srcIFC) sourceMultiplier = F12;
    // Get attribute time (always uses coefficient from custom attribute count)
    let attrTime = 0;
    if (attrRaumstempel) attrTime = F4_val * calculateAttrCoefficient(customAttrCount);else if (attrExcelFile) attrTime = F5_val * calculateAttrCoefficient(customAttrCount);
    // Base time calculation
    const baseTime = rooms / baseWorkspeed;
    // Formula: ((F1/E4)*F6*Fx + (F1/E4)*attrTime) * E1 * E5 * E3
    const calculation = (baseTime * difficulty * sourceMultiplier + baseTime * attrTime) * hourlyRate * management * marge;
    return calculation;
  }, [rooms, area, attrRaumstempel, attrExcelFile, difficulty, srcHandDrawn, srcDWG, srcPointcloud, srcHandDrawnPointcloud, srcDWGPointcloud, srcIFC, hourlyRate, provision, marge, baseWorkspeed, management, F3_val, F4_val, F5_val, F7, F8, F9, F10, F11, F12, customAttrCount]);
  // Calculate subtotal before marge (for Internal section display)
  const subtotal = useMemo(() => {
    // Get selected source material multiplier
    let sourceMultiplier = 0;
    if (srcHandDrawn) sourceMultiplier = F7;else if (srcDWG) sourceMultiplier = F8;else if (srcPointcloud) sourceMultiplier = F9;else if (srcHandDrawnPointcloud) sourceMultiplier = F10;else if (srcDWGPointcloud) sourceMultiplier = F11;else if (srcIFC) sourceMultiplier = F12;
    // Get attribute time (always uses coefficient from custom attribute count)
    let attrTime = 0;
    if (attrRaumstempel) attrTime = F4_val * calculateAttrCoefficient(customAttrCount);else if (attrExcelFile) attrTime = F5_val * calculateAttrCoefficient(customAttrCount);
    // Base time calculation
    const baseTime = rooms / baseWorkspeed;
    // Subtotal before marge: ((F1/E4)*F6*Fx + (F1/E4)*attrTime) * E1 * E5
    const calculation = (baseTime * difficulty * sourceMultiplier + baseTime * attrTime) * hourlyRate * management;
    return calculation;
  }, [rooms, attrRaumstempel, attrExcelFile, difficulty, srcHandDrawn, srcDWG, srcPointcloud, srcHandDrawnPointcloud, srcDWGPointcloud, srcIFC, hourlyRate, baseWorkspeed, management, F4_val, F5_val, F7, F8, F9, F10, F11, F12, customAttrCount]);
  // Login handler
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (username === 'iolabs' && password === 'iolabs123') {
      setIsAuthenticated(true);
      sessionStorage.setItem('authenticated', 'true');
      setLoginError('');
    } else {
      setLoginError(t.loginError);
    }
  };
  // Show login form if not authenticated
  if (!isAuthenticated) {
    return <div className="min-h-screen bg-white flex items-center justify-center p-4" data-id="element-5">
        <div className="bg-white border border-gray-400 rounded-lg shadow-sm p-8 w-full max-w-md" data-id="element-6">
          <h1 className="text-xl font-semibold text-gray-800 mb-6 text-center" data-id="element-7">
            {t.login}
          </h1>
          <form onSubmit={handleLogin} className="space-y-4" data-id="element-8">
            <div data-id="element-9">
              <label htmlFor="username" className="block text-sm font-medium text-gray-600 mb-1" data-id="element-10">
                {t.username}
              </label>
              <input id="username" type="text" value={username} onChange={e => setUsername(e.target.value)} className="w-full px-3 py-2 border border-gray-400 rounded-md focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand text-sm" required data-id="element-11" />
            </div>
            <div data-id="element-12">
              <label htmlFor="password" className="block text-sm font-medium text-gray-600 mb-1" data-id="element-13">
                {t.password}
              </label>
              <input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-3 py-2 border border-gray-400 rounded-md focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand text-sm" required data-id="element-14" />
            </div>
            {loginError && <div className="text-red-500 text-sm" data-id="element-15">{loginError}</div>}
            <button type="submit" className="w-full bg-brand text-white py-2 px-4 rounded-md hover:bg-brandDark transition-colors text-sm font-medium" data-id="element-16">
              {t.login}
            </button>
          </form>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-white p-4 font-sans text-sm text-gray-700" data-id="element-17">
      <div className={`mx-auto bg-white ${isPublicMode ? 'max-w-xl' : 'max-w-4xl'}`} data-id="element-18">
        {/* Header - hide in public mode */}
        {!isPublicMode && <div className="grid grid-cols-[1fr_150px_150px_150px] border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-19">
            <div className="p-2" data-id="element-20"></div>
            <div className="p-2 text-center" data-id="element-21">{t.exampleValue}</div>
            <div className="p-2 text-center" data-id="element-22">{t.calculationValue}</div>
            <div className="p-2 text-center" data-id="element-23">{t.result}</div>
          </div>}

        {/* Input Section */}
        <div className="border-b border-gray-200" data-id="element-24">
          <div className={`grid ${isPublicMode ? 'grid-cols-[1fr_150px]' : 'grid-cols-[1fr_150px_150px_150px]'} border-b border-gray-200`} data-id="element-25">
            <div className="p-2 font-semibold text-gray-800 border-l-2 border-l-brand pl-4" data-id="element-26">
              {t.input}
            </div>
            <div className="p-2" data-id="element-27"></div>
            {!isPublicMode && <>
                <div className="p-2 bg-gray-50" data-id="element-28"></div>
                <div className="p-2 bg-gray-50" data-id="element-29"></div>
              </>}
          </div>
          <div className={`grid ${isPublicMode ? 'grid-cols-[1fr_150px]' : 'grid-cols-[1fr_150px_150px_150px]'} border-b border-gray-100`} data-id="element-30">
            <div className="p-2 pl-8 text-gray-600" data-id="element-31">{t.rooms}</div>
            <div className="p-2" data-id="element-32">
              <input type="number" value={rooms} onChange={e => setRooms(Number(e.target.value))} className="w-full px-1 py-0.5 border border-gray-400 rounded text-right text-sm focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand" data-id="element-33" />
            </div>
            {!isPublicMode && <>
                <div className="p-2 bg-gray-50 text-right text-gray-600" data-id="element-34">
                  {rooms}
                </div>
                <div className="p-2 bg-gray-50" data-id="element-35"></div>
              </>}
          </div>
          <div className={`grid ${isPublicMode ? 'grid-cols-[1fr_150px]' : 'grid-cols-[1fr_150px_150px_150px]'}`} data-id="element-36">
            <div className="p-2 pl-8 text-gray-600" data-id="element-37">{t.area}</div>
            <div className="p-2" data-id="element-38">
              <input type="number" value={area} onChange={e => setArea(Number(e.target.value))} className="w-full px-1 py-0.5 border border-gray-400 rounded text-right text-sm focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand" data-id="element-39" />
            </div>
            {!isPublicMode && <>
                <div className="p-2 bg-gray-50 text-right text-gray-600" data-id="element-40">
                  {area}
                </div>
                <div className="p-2 bg-gray-50" data-id="element-41"></div>
              </>}
          </div>
        </div>

        {/* Attributes Section */}
        <div className="border-b border-gray-200" data-id="element-42">
          <div className={`grid ${isPublicMode ? 'grid-cols-[1fr_150px]' : 'grid-cols-[1fr_150px_150px_150px]'} border-b border-gray-200`} data-id="element-43">
            <div className="p-2 font-semibold text-gray-800 border-l-2 border-l-brand pl-4" data-id="element-44">
              {t.attributes}
            </div>
            <div className="p-2 text-center" data-id="element-45">
              <Checkbox checked={attributesEnabled} onChange={e => {
              setAttributesEnabled(e.target.checked);
              if (!e.target.checked) {
                setAttrRaumstempel(false);
                setAttrExcelFile(false);
              } else {
                setAttrRaumstempel(true);
              }
            }} data-id="element-46" />
            </div>
            {!isPublicMode && <>
                <div className="p-2 bg-gray-50" data-id="element-47"></div>
                <div className="p-2 bg-gray-50" data-id="element-48"></div>
              </>}
          </div>
          <div className={`grid ${isPublicMode ? 'grid-cols-[1fr_150px]' : 'grid-cols-[1fr_150px_150px_150px]'} border-b border-gray-100`} data-id="element-49">
            <div className="p-2 pl-8 text-gray-600" data-id="element-50">
              {t.attrRaumstempel}
            </div>
            <div className="p-2 text-center" data-id="element-51">
              <Checkbox checked={attrRaumstempel} onChange={e => {
              setAttrRaumstempel(e.target.checked);
              if (e.target.checked) {
                setAttrExcelFile(false);
              }
            }} disabled={!attributesEnabled} data-id="element-52" />
            </div>
            {!isPublicMode && <>
                <div className="p-2 bg-gray-50" data-id="element-53">
                  <input type="number" step="0.1" value={F4_val} onChange={e => setF4_val(Number(e.target.value))} className="w-full px-1 py-0.5 border border-gray-400 rounded text-right text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand" data-id="element-54" />
                </div>
                <div className="p-2 bg-gray-50 text-right text-gray-600" data-id="element-55">
                  {(F4_val * calculateAttrCoefficient(customAttrCount)).toFixed(2)}
                </div>
              </>}
          </div>
          <div className={`grid ${isPublicMode ? 'grid-cols-[1fr_150px]' : 'grid-cols-[1fr_150px_150px_150px]'} border-b border-gray-100`} data-id="element-56">
            <div className="p-2 pl-8 text-gray-600" data-id="element-57">{t.attrExcel}</div>
            <div className="p-2 text-center" data-id="element-58">
              <Checkbox checked={attrExcelFile} onChange={e => {
              setAttrExcelFile(e.target.checked);
              if (e.target.checked) {
                setAttrRaumstempel(false);
              }
            }} disabled={!attributesEnabled} data-id="element-59" />
            </div>
            {!isPublicMode && <>
                <div className="p-2 bg-gray-50" data-id="element-60">
                  <input type="number" step="0.1" value={F5_val} onChange={e => setF5_val(Number(e.target.value))} className="w-full px-1 py-0.5 border border-gray-400 rounded text-right text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand" data-id="element-61" />
                </div>
                <div className="p-2 bg-gray-50 text-right text-gray-600" data-id="element-62">
                  {(F5_val * calculateAttrCoefficient(customAttrCount)).toFixed(2)}
                </div>
              </>}
          </div>
          <div className={`grid ${isPublicMode ? 'grid-cols-[1fr_150px]' : 'grid-cols-[1fr_150px_150px_150px]'}`} data-id="element-63">
            <div className="p-2 pl-8 text-gray-600" data-id="element-64">
              {t.customAttrCount}
            </div>
            <div className="p-2" data-id="element-65">
              <input type="number" min="1" step="1" value={customAttrCount} onChange={e => setCustomAttrCount(Math.max(1, parseInt(e.target.value) || 1))} disabled={!attributesEnabled} className="w-full px-1 py-0.5 border border-gray-400 rounded text-right text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-40" data-id="element-66" />
            </div>
            {!isPublicMode && <>
                <div className="p-2 bg-gray-50 text-right text-gray-600" data-id="element-67">
                  {calculateAttrCoefficient(customAttrCount).toFixed(2)}
                </div>
                <div className="p-2 bg-gray-50" data-id="element-68"></div>
              </>}
          </div>
        </div>

        {/* Coefficient of difficulty */}
        <div className="border-b border-gray-200" data-id="element-69">
          <div className={`grid ${isPublicMode ? 'grid-cols-[1fr_150px]' : 'grid-cols-[1fr_150px_150px_150px]'} border-b border-gray-200`} data-id="element-70">
            <div className="p-2 font-semibold text-gray-800 border-l-2 border-l-brand pl-4" data-id="element-71">
              {t.coefficientOfDifficulty}
            </div>
            <div className="p-2" data-id="element-72"></div>
            {!isPublicMode && <>
                <div className="p-2 bg-gray-50" data-id="element-73"></div>
                <div className="p-2 bg-gray-50" data-id="element-74"></div>
              </>}
          </div>
          <div className={`grid ${isPublicMode ? 'grid-cols-[1fr_150px]' : 'grid-cols-[1fr_150px_150px_150px]'}`} data-id="element-75">
            <div className="p-2 pl-8 text-gray-500 text-xs" data-id="element-76">
              {t.difficultyDescription}
            </div>
            <div className="p-2" data-id="element-77">
              <input type="range" min="0.7" max="1.4" step="0.1" value={difficulty} onChange={e => setDifficulty(Number(e.target.value))} className="w-full accent-brand" data-id="element-78" />
              <div className="text-center text-sm font-medium text-gray-600 mt-1" data-id="element-79">
                {difficulty.toFixed(1)}
              </div>
            </div>
            {!isPublicMode && <>
                <div className="p-2 bg-gray-50" data-id="element-80"></div>
                <div className="p-2 bg-gray-50" data-id="element-81"></div>
              </>}
          </div>
        </div>

        {/* Source materials */}
        <div className="border-b border-gray-200" data-id="element-82">
          <div className={`grid ${isPublicMode ? 'grid-cols-[1fr_150px]' : 'grid-cols-[1fr_150px_150px_150px]'} border-b border-gray-200`} data-id="element-83">
            <div className="p-2 font-semibold text-gray-800 border-l-2 border-l-brand pl-4" data-id="element-84">
              {t.sourceMaterials}
            </div>
            <div className="p-2" data-id="element-85"></div>
            {!isPublicMode && <>
                <div className="p-2 bg-gray-50" data-id="element-86"></div>
                <div className="p-2 bg-gray-50" data-id="element-87"></div>
              </>}
          </div>
          <div className={`grid ${isPublicMode ? 'grid-cols-[1fr_150px]' : 'grid-cols-[1fr_150px_150px_150px]'} border-b border-gray-100`} data-id="element-88">
            <div className="p-2 pl-8 text-gray-600" data-id="element-89">{t.plansAndSections}</div>
            <div className="p-2" data-id="element-90"></div>
            {!isPublicMode && <>
                <div className="p-2 bg-gray-50" data-id="element-91"></div>
                <div className="p-2 bg-gray-50" data-id="element-92"></div>
              </>}
          </div>
          <div className={`grid ${isPublicMode ? 'grid-cols-[1fr_150px]' : 'grid-cols-[1fr_150px_150px_150px]'} border-b border-gray-100`} data-id="element-93">
            <div className="p-2 pl-12 text-gray-600" data-id="element-94">{t.handDrawn}</div>
            <div className="p-2 text-center" data-id="element-95">
              <Checkbox checked={srcHandDrawn} onChange={e => {
              setSrcHandDrawn(e.target.checked);
              if (e.target.checked) {
                setSrcDWG(false);
                setSrcPointcloud(false);
                setSrcHandDrawnPointcloud(false);
                setSrcDWGPointcloud(false);
                setSrcIFC(false);
              }
            }} data-id="element-96" />
            </div>
            {!isPublicMode && <>
                <div className="p-2 bg-gray-50" data-id="element-97">
                  <input type="number" step="0.1" value={F7} onChange={e => setF7(Number(e.target.value))} className="w-full px-1 py-0.5 border border-gray-400 rounded text-right text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand" data-id="element-98" />
                </div>
                <div className="p-2 bg-gray-50 text-right text-gray-600" data-id="element-99">
                  {(() => {
                const baseTime = rooms / baseWorkspeed;
                const attrTime = attrRaumstempel ? F4_val * calculateAttrCoefficient(customAttrCount) : attrExcelFile ? F5_val * calculateAttrCoefficient(customAttrCount) : 0;
                return ((baseTime * difficulty * F7 + baseTime * attrTime) * hourlyRate * management * marge).toFixed(2);
              })()}
                </div>
              </>}
          </div>
          <div className={`grid ${isPublicMode ? 'grid-cols-[1fr_150px]' : 'grid-cols-[1fr_150px_150px_150px]'} border-b border-gray-100`} data-id="element-100">
            <div className="p-2 pl-12 text-gray-600" data-id="element-101">{t.dwg}</div>
            <div className="p-2 text-center" data-id="element-102">
              <Checkbox checked={srcDWG} onChange={e => {
              setSrcDWG(e.target.checked);
              if (e.target.checked) {
                setSrcHandDrawn(false);
                setSrcPointcloud(false);
                setSrcHandDrawnPointcloud(false);
                setSrcDWGPointcloud(false);
                setSrcIFC(false);
              }
            }} data-id="element-103" />
            </div>
            {!isPublicMode && <>
                <div className="p-2 bg-gray-50" data-id="element-104">
                  <input type="number" step="0.1" value={F8} onChange={e => setF8(Number(e.target.value))} className="w-full px-1 py-0.5 border border-gray-400 rounded text-right text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand" data-id="element-105" />
                </div>
                <div className="p-2 bg-gray-50 text-right text-gray-600" data-id="element-106">
                  {(() => {
                const baseTime = rooms / baseWorkspeed;
                const attrTime = attrRaumstempel ? F4_val * calculateAttrCoefficient(customAttrCount) : attrExcelFile ? F5_val * calculateAttrCoefficient(customAttrCount) : 0;
                return ((baseTime * difficulty * F8 + baseTime * attrTime) * hourlyRate * management * marge).toFixed(2);
              })()}
                </div>
              </>}
          </div>
          <div className={`grid ${isPublicMode ? 'grid-cols-[1fr_150px]' : 'grid-cols-[1fr_150px_150px_150px]'} border-b border-gray-100`} data-id="element-107">
            <div className="p-2 pl-8 text-gray-600" data-id="element-108">{t.pointcloud}</div>
            <div className="p-2 text-center" data-id="element-109">
              <Checkbox checked={srcPointcloud} onChange={e => {
              setSrcPointcloud(e.target.checked);
              if (e.target.checked) {
                setSrcHandDrawn(false);
                setSrcDWG(false);
                setSrcHandDrawnPointcloud(false);
                setSrcDWGPointcloud(false);
                setSrcIFC(false);
              }
            }} data-id="element-110" />
            </div>
            {!isPublicMode && <>
                <div className="p-2 bg-gray-50" data-id="element-111">
                  <input type="number" step="0.1" value={F9} onChange={e => setF9(Number(e.target.value))} className="w-full px-1 py-0.5 border border-gray-400 rounded text-right text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand" data-id="element-112" />
                </div>
                <div className="p-2 bg-gray-50 text-right text-gray-600" data-id="element-113">
                  {(() => {
                const baseTime = rooms / baseWorkspeed;
                const attrTime = attrRaumstempel ? F4_val * calculateAttrCoefficient(customAttrCount) : attrExcelFile ? F5_val * calculateAttrCoefficient(customAttrCount) : 0;
                return ((baseTime * difficulty * F9 + baseTime * attrTime) * hourlyRate * management * marge).toFixed(2);
              })()}
                </div>
              </>}
          </div>
          <div className={`grid ${isPublicMode ? 'grid-cols-[1fr_150px]' : 'grid-cols-[1fr_150px_150px_150px]'} border-b border-gray-100`} data-id="element-114">
            <div className="p-2 pl-8 text-gray-600" data-id="element-115">
              {t.plansPointcloud}
            </div>
            <div className="p-2" data-id="element-116"></div>
            {!isPublicMode && <>
                <div className="p-2 bg-gray-50" data-id="element-117"></div>
                <div className="p-2 bg-gray-50" data-id="element-118"></div>
              </>}
          </div>
          <div className={`grid ${isPublicMode ? 'grid-cols-[1fr_150px]' : 'grid-cols-[1fr_150px_150px_150px]'} border-b border-gray-100`} data-id="element-119">
            <div className="p-2 pl-12 text-gray-600" data-id="element-120">
              {t.handDrawnPointcloud}
            </div>
            <div className="p-2 text-center" data-id="element-121">
              <Checkbox checked={srcHandDrawnPointcloud} onChange={e => {
              setSrcHandDrawnPointcloud(e.target.checked);
              if (e.target.checked) {
                setSrcHandDrawn(false);
                setSrcDWG(false);
                setSrcPointcloud(false);
                setSrcDWGPointcloud(false);
                setSrcIFC(false);
              }
            }} data-id="element-122" />
            </div>
            {!isPublicMode && <>
                <div className="p-2 bg-gray-50" data-id="element-123">
                  <input type="number" step="0.1" value={F10} onChange={e => setF10(Number(e.target.value))} className="w-full px-1 py-0.5 border border-gray-400 rounded text-right text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand" data-id="element-124" />
                </div>
                <div className="p-2 bg-gray-50 text-right text-gray-600" data-id="element-125">
                  {(() => {
                const baseTime = rooms / baseWorkspeed;
                const attrTime = attrRaumstempel ? F4_val * calculateAttrCoefficient(customAttrCount) : attrExcelFile ? F5_val * calculateAttrCoefficient(customAttrCount) : 0;
                return ((baseTime * difficulty * F10 + baseTime * attrTime) * hourlyRate * management * marge).toFixed(2);
              })()}
                </div>
              </>}
          </div>
          <div className={`grid ${isPublicMode ? 'grid-cols-[1fr_150px]' : 'grid-cols-[1fr_150px_150px_150px]'} border-b border-gray-100`} data-id="element-126">
            <div className="p-2 pl-12 text-gray-600" data-id="element-127">{t.dwgPointcloud}</div>
            <div className="p-2 text-center" data-id="element-128">
              <Checkbox checked={srcDWGPointcloud} onChange={e => {
              setSrcDWGPointcloud(e.target.checked);
              if (e.target.checked) {
                setSrcHandDrawn(false);
                setSrcDWG(false);
                setSrcPointcloud(false);
                setSrcHandDrawnPointcloud(false);
                setSrcIFC(false);
              }
            }} data-id="element-129" />
            </div>
            {!isPublicMode && <>
                <div className="p-2 bg-gray-50" data-id="element-130">
                  <input type="number" step="0.1" value={F11} onChange={e => setF11(Number(e.target.value))} className="w-full px-1 py-0.5 border border-gray-400 rounded text-right text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand" data-id="element-131" />
                </div>
                <div className="p-2 bg-gray-50 text-right text-gray-600" data-id="element-132">
                  {(() => {
                const baseTime = rooms / baseWorkspeed;
                const attrTime = attrRaumstempel ? F4_val * calculateAttrCoefficient(customAttrCount) : attrExcelFile ? F5_val * calculateAttrCoefficient(customAttrCount) : 0;
                return ((baseTime * difficulty * F11 + baseTime * attrTime) * hourlyRate * management * marge).toFixed(2);
              })()}
                </div>
              </>}
          </div>
          <div className={`grid ${isPublicMode ? 'grid-cols-[1fr_150px]' : 'grid-cols-[1fr_150px_150px_150px]'}`} data-id="element-133">
            <div className="p-2 pl-8 text-gray-600" data-id="element-134">{t.ifcImport}</div>
            <div className="p-2 text-center" data-id="element-135">
              <Checkbox checked={srcIFC} onChange={e => {
              setSrcIFC(e.target.checked);
              if (e.target.checked) {
                setSrcHandDrawn(false);
                setSrcDWG(false);
                setSrcPointcloud(false);
                setSrcHandDrawnPointcloud(false);
                setSrcDWGPointcloud(false);
              }
            }} data-id="element-136" />
            </div>
            {!isPublicMode && <>
                <div className="p-2 bg-gray-50" data-id="element-137">
                  <input type="number" step="0.1" value={F12} onChange={e => setF12(Number(e.target.value))} className="w-full px-1 py-0.5 border border-gray-400 rounded text-right text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand" data-id="element-138" />
                </div>
                <div className="p-2 bg-gray-50 text-right text-gray-600" data-id="element-139">
                  {(() => {
                const baseTime = rooms / baseWorkspeed;
                const attrTime = attrRaumstempel ? F4_val * calculateAttrCoefficient(customAttrCount) : attrExcelFile ? F5_val * calculateAttrCoefficient(customAttrCount) : 0;
                return ((baseTime * difficulty * F12 + baseTime * attrTime) * hourlyRate * management * marge).toFixed(2);
              })()}
                </div>
              </>}
          </div>
        </div>

        {/* Internal - only show in non-public mode */}
        {!isPublicMode && <div className="border-b border-gray-200" data-id="element-140">
            <div className="grid grid-cols-[1fr_150px_150px_150px] border-b border-gray-200" data-id="element-141">
              <div className="p-2 font-semibold text-gray-800 border-l-2 border-l-brand pl-4" data-id="element-142">
                {t.internal}
              </div>
              <div className="p-2" data-id="element-143"></div>
              <div className="p-2 bg-gray-50 text-right font-medium text-gray-700" data-id="element-144">
                {subtotal.toFixed(2)}
              </div>
              <div className="p-2 bg-gray-50" data-id="element-145"></div>
            </div>

            <div className="grid grid-cols-[1fr_150px_150px_150px] border-b border-gray-100 bg-gray-50/50" data-id="element-146">
              <div className="p-2 pl-8 text-gray-600" data-id="element-147">{t.internalHourlyRate}</div>
              <div className="p-2" data-id="element-148"></div>
              <div className="p-2" data-id="element-149">
                <input type="number" step="0.01" value={hourlyRate} onChange={e => setHourlyRate(Number(e.target.value))} className="w-full px-1 py-0.5 border border-gray-400 rounded text-right text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand" data-id="element-150" />
              </div>
              <div className="p-2" data-id="element-151"></div>
            </div>
            <div className="grid grid-cols-[1fr_150px_150px_150px] border-b border-gray-100 bg-gray-50/50" data-id="element-152">
              <div className="p-2 pl-8 text-gray-600" data-id="element-153">{t.provision}</div>
              <div className="p-2 text-right text-gray-500" data-id="element-154">
                {(subtotal * (marge - 1) * provision).toFixed(2)}
              </div>
              <div className="p-2" data-id="element-155">
                <input type="number" step="0.01" value={provision} onChange={e => setProvision(Number(e.target.value))} className="w-full px-1 py-0.5 border border-gray-400 rounded text-right text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand" data-id="element-156" />
              </div>
              <div className="p-2" data-id="element-157"></div>
            </div>
            <div className="grid grid-cols-[1fr_150px_150px_150px] border-b border-gray-100 bg-gray-50/50" data-id="element-158">
              <div className="p-2 pl-8 text-gray-600" data-id="element-159">{t.marge}</div>
              <div className="p-2 text-right text-gray-500" data-id="element-160">
                {(subtotal * (marge - 1)).toFixed(2)}
              </div>
              <div className="p-2" data-id="element-161">
                <input type="number" step="0.01" value={marge} onChange={e => setMarge(Number(e.target.value))} className="w-full px-1 py-0.5 border border-gray-400 rounded text-right text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand" data-id="element-162" />
              </div>
              <div className="p-2" data-id="element-163"></div>
            </div>
            <div className="grid grid-cols-[1fr_150px_150px_150px] border-b border-gray-100 bg-gray-50/50" data-id="element-164">
              <div className="p-2 pl-8 text-gray-600" data-id="element-165">{t.partIoLabs}</div>
              <div className="p-2 text-right text-gray-500" data-id="element-166">
                {(subtotal * (marge - 1) - subtotal * (marge - 1) * provision).toFixed(2)}
              </div>
              <div className="p-2" data-id="element-167"></div>
              <div className="p-2" data-id="element-168"></div>
            </div>
            <div className="grid grid-cols-[1fr_150px_150px_150px] border-b border-gray-100 bg-gray-50/50" data-id="element-169">
              <div className="p-2 pl-8 text-gray-600" data-id="element-170">
                {t.baseWorkspeed}
              </div>
              <div className="p-2" data-id="element-171"></div>
              <div className="p-2" data-id="element-172">
                <input type="number" step="0.01" value={baseWorkspeed} onChange={e => setBaseWorkspeed(Number(e.target.value))} className="w-full px-1 py-0.5 border border-gray-400 rounded text-right text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand" data-id="element-173" />
              </div>
              <div className="p-2" data-id="element-174"></div>
            </div>
            <div className="grid grid-cols-[1fr_150px_150px_150px] bg-gray-50/50" data-id="element-175">
              <div className="p-2 pl-8 text-gray-600" data-id="element-176">{t.management}</div>
              <div className="p-2" data-id="element-177"></div>
              <div className="p-2" data-id="element-178">
                <input type="number" step="0.01" value={management} onChange={e => setManagement(Number(e.target.value))} className="w-full px-1 py-0.5 border border-gray-400 rounded text-right text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand" data-id="element-179" />
              </div>
              <div className="p-2" data-id="element-180"></div>
            </div>
          </div>}

        {/* Result */}
        <div className="mt-2" data-id="element-181">
          <div className={`grid ${isPublicMode ? 'grid-cols-[1fr_150px]' : 'grid-cols-[1fr_150px_150px_150px]'}`} data-id="element-182">
            <div className="p-2" data-id="element-183"></div>
            <div className={`p-2 ${isPublicMode ? 'bg-brand/10 text-right font-semibold text-brand text-lg rounded' : ''}`} data-id="element-184">
              {isPublicMode && <>
                  {formatCHF(Math.max(result, 750))}
                  <div className="text-xs font-normal text-gray-500 mt-0.5" data-id="element-185">
                    {t.exclVat}
                  </div>
                </>}
            </div>
            {!isPublicMode && <>
                <div className="p-2 bg-brand/10 text-right font-semibold text-brand text-lg rounded" data-id="element-186">
                  {formatCHF(Math.max(result, 750))}
                  <div className="text-xs font-normal text-gray-500 mt-0.5" data-id="element-187">
                    {t.exclVat}
                  </div>
                </div>
              </>}
          </div>
        </div>
      </div>
    </div>;
}